import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, finalize, Observable, Subject, tap } from 'rxjs';
import {
  blockingLoaderHeaders,
  skipLoaderHeaders,
} from 'app/core/interceptors/loader.interceptor';
import { environment } from 'environments/environment';

import {
  Roles,
  User,
  UserInvitationPayload,
  UserInvitationResult,
  UsersPaginated,
} from '../../interfaces/User';
import { Company } from '../../interfaces/Company';
import { ApiResponse } from '../../interfaces/httpResponses';
import { NotificationService } from '../shared/handlers/notification.service';
import { clearStoredPaymentCommands } from '../shared/payment-command-store.service';
import { clearStoredListStates } from '../shared/list-state.service';
import { clearStoredFormDrafts } from '../shared/form-draft-storage.service';
import { PLATFORM_NOTICES_SESSION_KEY } from './platform-announcements.service';

/**
 * The v2 login and `/me` send the user's id as `id`; the app reads `_id`.
 * Sessions stored with only `id` would otherwise look logged out to every
 * `_id` check (and the permissions would fall back to legacy: allow all).
 */
function withUserId(user: User): User {
  const { id } = user as User & { id?: string };
  return user._id || !id ? user : { ...user, _id: id };
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly ACCOUNT_URL: string = `${environment.apiUrl}user/account`;
  private readonly USER_DATA: string = 'user';
  private readonly COMPANY_DATA: string = 'company';
  private readonly ACCESS_TOKEN: string = 'access_token';

  private _usersInCompany = new Subject<UsersPaginated>();
  public usersInCompany = this._usersInCompany.asObservable();

  private _sessionChanged$ = new Subject<void>();
  /** Emits whenever the stored user/session is written or cleared. */
  public sessionChanged$ = this._sessionChanged$.asObservable();

  private _isLoading$ = new BehaviorSubject<boolean>(false);
  public isLoading = this._isLoading$.asObservable();

  private _company$ = new BehaviorSubject<Company | null>(null);
  /** Emits the stored company on load and whenever the session changes. */
  public company$ = this._company$.asObservable();

  private _companyLogo$ = new BehaviorSubject<string | null>(null);
  public companyLogo$ = this._companyLogo$.asObservable();

  constructor(
    private httpClient: HttpClient,
    private notificationService: NotificationService,
  ) {
    this.syncCompanyLogoUrl();
    this._company$.next(this.readStoredCompany());
  }

  private readStoredCompany(): Company | null {
    const company = this.companyData;
    return company?._id ? company : null;
  }

  public syncCompanyLogoUrl(company: Company = this.companyData): void {
    const logoUrl = company?.logo?.trim();

    this._companyLogo$.next(logoUrl || null);
  }

  public clearCompanyLogoUrl(): void {
    this._companyLogo$.next(null);
  }

  public setCompanyLogoInMemory(logoUrl: string | null): void {
    this._companyLogo$.next(logoUrl);
  }

  public clearCompanyLogoInMemory(): void {
    this._companyLogo$.next(null);
  }

  private getJsonFromStorage<T>(key: string): T | null {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    try {
      return JSON.parse(raw) as T;
    } catch {
      localStorage.removeItem(key);
      this.notificationService.showToastError(
        'Se detectaron datos locales inválidos. Se limpiaron para evitar errores.',
      );
      return null;
    }
  }

  private isQuotaExceededError(e: unknown): boolean {
    const err = e as { name?: string; message?: string };
    return (
      err?.name === 'QuotaExceededError' ||
      (err?.message || '').toLowerCase().includes('exceeded the quota')
    );
  }

  clearSession(): void {
    clearStoredPaymentCommands();
    clearStoredListStates();
    clearStoredFormDrafts();
    localStorage.removeItem(this.USER_DATA);
    localStorage.removeItem(this.COMPANY_DATA);
    localStorage.removeItem(this.ACCESS_TOKEN);
    // A new login (or logout) must show pending platform notices again.
    sessionStorage.removeItem(PLATFORM_NOTICES_SESSION_KEY);
    this.clearCompanyLogoUrl();
    this._company$.next(null);
    this._sessionChanged$.next();
  }

  persistSession(user: User, company: Company, token: string): boolean {
    const userWithCompany: User = {
      ...withUserId(user),
      company,
    };

    const write = (): void => {
      localStorage.setItem(this.USER_DATA, JSON.stringify(userWithCompany));
      localStorage.setItem(this.COMPANY_DATA, JSON.stringify(company));
      localStorage.setItem(this.ACCESS_TOKEN, token);
    };

    try {
      this.clearSession();
      write();
      this.syncCompanyLogoUrl(company);
      this._company$.next(company);
      this._sessionChanged$.next();

      return true;
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        this.notificationService.showToastError(
          'No se pudo guardar la sesión: el almacenamiento del navegador está lleno.',
        );
      } else {
        this.notificationService.showToastError(
          'No se pudo guardar la sesión local.',
        );
      }

      this.clearSession();

      return false;
    }
  }

  get userData(): User {
    const user = this.getJsonFromStorage<User>(this.USER_DATA);
    return user ? withUserId(user) : ({} as User);
  }

  get userId(): string {
    return this.userData?._id ?? '';
  }

  get companyData(): Company {
    return (
      this.getJsonFromStorage<Company>(this.COMPANY_DATA) ?? ({} as Company)
    );
  }

  get isAdmin(): boolean {
    return this.hasRole(Roles.ADMIN);
  }

  get isPlatformAdmin(): boolean {
    return this.userData?.isPlatformAdmin === true;
  }

  setUserData(user: User): void {
    try {
      localStorage.setItem(this.USER_DATA, JSON.stringify(withUserId(user)));
      this._sessionChanged$.next();
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        this.notificationService.showToastError(
          'No se pudo guardar el usuario: el almacenamiento del navegador está lleno.',
        );
      }
    }
  }

  setCompanyData(incoming: Company): void {
    // Endpoints that update the company do not echo `subscription`: keep the
    // one from the session so the banner does not disappear.
    const company: Company = incoming.subscription
      ? incoming
      : { ...incoming, subscription: this.companyData.subscription };
    try {
      localStorage.setItem(this.COMPANY_DATA, JSON.stringify(company));

      const user = this.userData;

      if (user?._id) {
        localStorage.setItem(
          this.USER_DATA,
          JSON.stringify({
            ...user,
            company,
          }),
        );
      }

      this.syncCompanyLogoUrl(company);
      this._company$.next(company);
    } catch (error) {
      if (this.isQuotaExceededError(error)) {
        this.notificationService.showToastError(
          'No se pudo guardar la empresa: el almacenamiento del navegador está lleno.',
        );
      }
    }
  }

  setAccessToken(token: string): void {
    try {
      localStorage.setItem(this.ACCESS_TOKEN, token);
    } catch (e) {
      if (this.isQuotaExceededError(e)) {
        this.notificationService.showToastError(
          'No se pudo guardar el token (almacenamiento del navegador lleno).',
        );
      }
    }
  }

  loadUsersInCompany(options?: { limit: number; page: number }): void {
    this._isLoading$.next(true);

    const finalOptions = options ?? { limit: 10, page: 1 };
    const params = new HttpParams()
      .set('limit', finalOptions.limit.toString())
      .set('page', finalOptions.page.toString());

    this.httpClient
      .get<ApiResponse<UsersPaginated>>(`${this.ACCOUNT_URL}/company/users`, {
        params,
        headers: skipLoaderHeaders(),
      })
      .subscribe({
        next: (response: ApiResponse<UsersPaginated>) => {
          this._usersInCompany.next(response.payload);
          this._isLoading$.next(false);
        },
        error: () => this._isLoading$.next(false),
      });
  }

  updateCompanyData(company: Company, logoFile?: File | null): void {
    this._isLoading$.next(true);

    const companyPayload: Partial<Company> = { ...company };
    delete companyPayload._id;
    delete companyPayload.logo;

    const body = logoFile
      ? this.buildCompanyFormData(companyPayload as Company, logoFile)
      : { company: companyPayload };

    this.httpClient
      .put<ApiResponse<Company>>(`${this.ACCOUNT_URL}/company`, body, {
        headers: skipLoaderHeaders(),
      })
      .subscribe({
        next: (response: ApiResponse<Company>) => {
          this.setCompanyData(response.payload);

          this.notificationService.showTotast(
            'Información de la empresa actualizada',
          );
        },
        error: (error: unknown) => {
          this.notificationService.showToastError(
            error,
            'Error al actualizar la información de la empresa',
          );
        },
        complete: () => this._isLoading$.next(false),
      });
  }

  createUser(
    user: UserInvitationPayload,
  ): Observable<ApiResponse<UserInvitationResult>> {
    this._isLoading$.next(true);
    return this.httpClient
      .post<ApiResponse<UserInvitationResult>>(
        `${this.ACCOUNT_URL}/company/${this.companyData._id}/create`,
        {
          user,
        },
        { headers: skipLoaderHeaders() },
      )
      .pipe(
        tap(() => this.loadUsersInCompany()),
        finalize(() => this._isLoading$.next(false)),
      );
  }

  resendUserInvitation(user: User): Observable<ApiResponse<string>> {
    this._isLoading$.next(true);
    return this.httpClient
      .post<
        ApiResponse<string>
      >(`${this.ACCOUNT_URL}/company/users/${user._id}/resend-invitation`, {}, { headers: skipLoaderHeaders() })
      .pipe(finalize(() => this._isLoading$.next(false)));
  }

  updateCurrentUser(user: {
    name: string;
    email: string;
    password: string;
    confirmPassword: string;
  }): void {
    this._isLoading$.next(true);

    this.httpClient
      .put<ApiResponse<User>>(
        `${this.ACCOUNT_URL}/user`,
        { user },
        {
          // Personal data / password change: no local indicator on the form.
          headers: blockingLoaderHeaders({ message: 'Guardando tus datos...' }),
        },
      )
      .subscribe({
        next: (response) => {
          this.setUserData({
            ...this.userData,
            ...response.payload,
            company: this.companyData,
          });
          this.notificationService.showSuccess(
            'Tus datos fueron actualizados. Por favor, vuelve a iniciar sesión.',
          );
        },
        error: (error: unknown) => {
          this.notificationService.showToastError(
            error,
            'Error al actualizar tus datos.',
          );
        },
        complete: () => this._isLoading$.next(false),
      });
  }

  resetUserPassword(user: User): Observable<ApiResponse<string>> {
    this._isLoading$.next(true);
    return this.httpClient
      .put<
        ApiResponse<string>
      >(`${this.ACCOUNT_URL}/company/${this.companyData._id}/user/${user._id}/reset`, { user }, { headers: skipLoaderHeaders() })
      .pipe(finalize(() => this._isLoading$.next(false)));
  }

  hasRole(role: string): boolean {
    return this.userData?.role === role;
  }

  private buildCompanyFormData(company: Company, logoFile: File): FormData {
    const formData = new FormData();

    formData.append('company', JSON.stringify(company));
    formData.append('logo', logoFile);

    return formData;
  }
}
